"use client"

import { useState, useEffect, useCallback } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import FollowButton from '@/components/follow-button'
import FollowRequests from '@/components/follow-requests'
import VideoGrid from '@/components/video-grid'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  ArrowLeft,
  BadgeCheck,
  Calendar,
  Crown,
  Image as ImageIcon,
  Loader2,
  Lock,
  Music,
  Receipt,
  Sparkles,
  Trash2,
  Unlock,
  Upload,
  UserCheck,
  UserX,
  Users,
  Video as VideoIcon,
  Wand2,
  Search,
  X,
} from 'lucide-react'
import Link from 'next/link'
import { apiService, User, Video, VideoDetail } from '@/lib/api'
import { getMediaUrl } from '@/lib/api'
import {
  extractCategoryName,
  generateGifUrl,
  generateMemeTemplateUrl,
  generateSoundEffectUrl,
  generateTitleSlug,
  generateVideoUrl,
  cn,
} from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { usePremiumSubscription } from '@/hooks/use-premium-subscription'
import { useAuth } from '@/contexts/auth-context'
import { useAuthCheck } from '@/hooks/use-auth-check'
import AuthPopup from '@/components/auth-popup'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { UserProfileUploadGrid } from './user-profile-upload-grid'

function isPaginatedUserSearch(
  res: unknown
): res is { count: number; next: string | null; previous: string | null; results: unknown[] } {
  return (
    typeof res === 'object' &&
    res !== null &&
    Array.isArray((res as { results?: unknown }).results)
  )
}

/** Maps upload tab to GET /users/:id/search `type` param (sound-effects excluded — not supported). */
function mapContentTypeToSearchApiType(
  ct: 'meme-videos' | 'meme-templates' | 'gifs' | 'sound-effects' | 'generated-memes'
): 'video' | 'template' | 'gif' | 'generatedmeme' | null {
  switch (ct) {
    case 'meme-videos':
      return 'video'
    case 'meme-templates':
      return 'template'
    case 'gifs':
      return 'gif'
    case 'generated-memes':
      return 'generatedmeme'
    default:
      return null
  }
}

interface UserProfile {
  id: number
  user: User
  profilePicturePath?: string
  is_private?: boolean
  verified?: boolean
  has_premium_plan?: boolean
  is_premium?: boolean
  premium?: boolean
  following_count: number
  followers_count: number
  created_at: string
  updated_at: string
}

interface UserProfileClientProps {
  userProfile: UserProfile
  userVideos: Video[]
}

export default function UserProfileClient({
  userProfile,
  userVideos
}: UserProfileClientProps) {
  const router = useRouter()
  const { toast } = useToast()
  const { user: authUser } = useAuth()
  const { checkAuth, showAuthPopup, authAction, closeAuthPopup } = useAuthCheck()
  const [followers, setFollowers] = useState<User[]>([])
  const [following, setFollowing] = useState<User[]>([])
  const [activeTab, setActiveTab] = useState<'videos' | 'followers' | 'following' | 'requests' | 'uploads'>('videos')
  const [isFollowing, setIsFollowing] = useState(false)
  const [isPrivate, setIsPrivate] = useState(userProfile.is_private || false)
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false)
  const [isOwnProfileState, setIsOwnProfileState] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)
  const [isBlocking, setIsBlocking] = useState(false)
  const { subscription: premiumSubscription, isPremium, hasFeature } = usePremiumSubscription()

  const [userContent, setUserContent] = useState<any[]>([])
  const [contentType, setContentType] = useState<
    'meme-videos' | 'meme-templates' | 'gifs' | 'sound-effects' | 'generated-memes'
  >('meme-videos')
  const [contentLoading, setContentLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  // Holds the item the user has requested to delete so the confirmation
  // dialog can show its title and so we know what to delete on confirm.
  const [pendingDelete, setPendingDelete] = useState<{ id: number; title: string } | null>(null)

  /** Controlled search field; debounced into `debouncedSearch` for API calls. */
  const [uploadSearchInput, setUploadSearchInput] = useState('')
  const [debouncedUploadSearch, setDebouncedUploadSearch] = useState('')
  /** Pagination for GET /users/:id/search (cursor is `next` URL string or null). */
  const [searchNext, setSearchNext] = useState<string | null>(null)
  const [searchPage, setSearchPage] = useState(1)
  const [profilePremiumFromApi, setProfilePremiumFromApi] = useState(() =>
    Boolean(
      userProfile.has_premium_plan ||
      userProfile.is_premium ||
      userProfile.premium ||
      userProfile.user.has_premium_plan ||
      userProfile.user.is_premium ||
      userProfile.user.premium
    )
  )

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedUploadSearch(uploadSearchInput.trim()), 380)
    return () => window.clearTimeout(t)
  }, [uploadSearchInput])

  const getToken = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token')
    }
    return null
  }

  const checkOwnProfile = () => {
    if (typeof window === 'undefined') return false
    const localUserId = localStorage.getItem('no')
    if (localUserId) return localUserId === String(userProfile.id)
    if (authUser?.id != null) return String(authUser.id) === String(userProfile.id)
    return false
  }

  useEffect(() => {
    setIsMounted(true)
    setIsOwnProfileState(checkOwnProfile())
  }, [userProfile.id, authUser?.id])

  // Re-fetch profile with auth token — API may expose has_premium_plan only when authenticated.
  useEffect(() => {
    if (!isMounted) return
    let cancelled = false
    ;(async () => {
      try {
        const token = getToken()
        const fresh = await apiService.getUserProfileData(userProfile.id, token)
        if (cancelled) return
        setProfilePremiumFromApi(
          Boolean(
            fresh.has_premium_plan ||
            fresh.is_premium ||
            fresh.premium ||
            fresh.user?.has_premium_plan ||
            fresh.user?.is_premium ||
            fresh.user?.premium
          )
        )
      } catch {
        /* keep SSR value */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isMounted, userProfile.id, authUser?.id])

  const pathname = usePathname()
  const targetUserId = userProfile.user?.id ?? userProfile.id

  useEffect(() => {
    if (!isMounted) return
    if (!isOwnProfileState) return
    if (pathname.startsWith('/user/')) router.replace('/profile')
  }, [isMounted, isOwnProfileState, pathname, router])

  useEffect(() => {
    if (!isMounted) return
    if (isOwnProfileState) return
    if (activeTab === 'videos' || activeTab === 'requests') {
      setActiveTab('uploads')
    }
  }, [activeTab, isMounted, isOwnProfileState])

  useEffect(() => {
    if (!isMounted) return
    if (!isOwnProfileState) return
    if (pathname !== '/profile') return
    if (activeTab === 'videos') setActiveTab('uploads')
  }, [activeTab, isMounted, isOwnProfileState, pathname])

  useEffect(() => {
    setIsPrivate(userProfile.is_private || false)
  }, [userProfile.is_private])

  useEffect(() => {
    const fetchUserData = async () => {
      const token = getToken()
      const ownProfile = isOwnProfileState

      if (!token || ownProfile) {
        setIsBlocked(false)
      }

      if (token && !ownProfile && pathname.startsWith('/user/')) {
        try {
          const blocked = await apiService.getBlockedUsers(token).catch(() => null)
          const blockedIds = blocked?.results?.map((u) => u.id) ?? []
          const currentlyBlocked = blockedIds.includes(targetUserId)
          setIsBlocked(currentlyBlocked)
          if (currentlyBlocked) setIsFollowing(false)
        } catch {
          setIsBlocked(false)
        }

        try {
          const res: any = await apiService.checkFollowStatus(targetUserId, token)
          if (res && typeof res === 'object') {
            if ('status' in res) {
              setIsFollowing(res.status === 'accepted' || res.is_following === true)
            } else if ('is_following' in res) {
              setIsFollowing(res.is_following === true)
            } else {
              setIsFollowing(false)
            }
          } else {
            setIsFollowing(false)
          }
        } catch (error) {
          console.error('Error checking follow status:', error)
        }
      }

      if (token) {
        try {
          const [followersData, followingData] = await Promise.all([
            apiService.getUserFollowers(userProfile.id, token).catch(err => {
              console.error('Error fetching followers:', err)
              return []
            }),
            apiService.getUserFollowing(userProfile.id, token).catch(err => {
              console.error('Error fetching following:', err)
              return []
            })
          ])
          setFollowers(Array.isArray(followersData) ? followersData : [])
          setFollowing(Array.isArray(followingData) ? followingData : [])
        } catch (error) {
          console.error('Error fetching followers/following:', error)
          setFollowers([])
          setFollowing([])
        }
      } else {
        setFollowers([])
        setFollowing([])
      }
    }

    fetchUserData()
  }, [userProfile.id, isOwnProfileState, targetUserId])

  const handleBlockToggle = async () => {
    await checkAuth("block users", async () => {
      const token = getToken()
      if (!token) return

      setIsBlocking(true)
      try {
        if (isBlocked) {
          await apiService.unblockUser(targetUserId, token)
          setIsBlocked(false)
          toast({
            title: 'User unblocked',
            description: 'You can now interact with this user again.',
          })
        } else {
          await apiService.blockUser(targetUserId, token)
          setIsBlocked(true)
          setIsFollowing(false)
          toast({
            title: 'User blocked',
            description: 'You will no longer see content from this user.',
          })
        }
      } catch (error: any) {
        toast({
          title: isBlocked ? 'Unblock failed' : 'Block failed',
          description: error?.message || 'Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsBlocking(false)
      }
    })
  }

  const handleFollowChange = (following: boolean) => {
    setIsFollowing(following)
  }

  const handleVideoClick = (video: Video | VideoDetail) => {
    const categoryName = extractCategoryName(video)
    const videoUrl = generateVideoUrl(categoryName, video.id, video.title)
    router.push(videoUrl)
  }

  const handleItemClick = (item: any) => {
    try {
      let url = ''
      switch (contentType) {
        case 'meme-videos': {
          const catName = extractCategoryName(item)
          url = generateVideoUrl(catName, item.id, item.title)
          break
        }
        case 'meme-templates': {
          url = generateMemeTemplateUrl(item.id, item.title)
          break
        }
        case 'gifs': {
          url = generateGifUrl(item.id, item.title)
          break
        }
        case 'sound-effects': {
          url = generateSoundEffectUrl(item.id, item.title || item.name || 'sound')
          break
        }
        case 'generated-memes': {
          // Open the meme in the v2 editor so the user can remix or share it.
          const memeSlug =
            (typeof item.slug === 'string' && item.slug) ||
            generateTitleSlug(item.title || 'meme') ||
            String(item.id)
          url = `/meme-generator/${memeSlug}`
          break
        }
      }
      if (url) router.push(url)
    } catch (error) {
      console.error('Error generating URL:', error)
    }
  }

  // Opens the confirmation dialog with the item we're about to delete.
  // The actual API call happens in `confirmDeleteContent` once the user
  // confirms (or never, if they cancel).
  const requestDeleteContent = (item: { id: number; title?: string | null }) => {
    setPendingDelete({
      id: item.id,
      title: (item.title || '').trim() || 'this item',
    })
  }

  const confirmDeleteContent = async () => {
    if (!pendingDelete) return
    const token = getToken()
    if (!token) {
      toast({
        title: 'Sign in required',
        description: 'Please log in again to delete this content.',
        variant: 'destructive',
      })
      return
    }

    const { id } = pendingDelete
    setDeletingId(id)
    try {
      await apiService.deleteContent(contentType, id, token)
      setUserContent((prev) => prev.filter((item) => item.id !== id))
      setPendingDelete(null)
      toast({
        title: 'Content deleted',
        description: 'Your content has been removed successfully.',
      })
    } catch (error: any) {
      // Surface the error; ConfirmDialog will keep itself open on throw so
      // the user can retry without losing context.
      toast({
        title: 'Delete failed',
        description: error?.message || 'Failed to delete content',
        variant: 'destructive',
      })
      throw error
    } finally {
      setDeletingId(null)
    }
  }

  const handlePrivacyToggle = async (checked: boolean) => {
    if (!isOwnProfileState) return
    
    const token = getToken()
    if (!token) {
      toast({
        title: 'Error',
        description: 'Please log in to update your privacy settings',
        variant: 'destructive',
      })
      return
    }

    setIsUpdatingPrivacy(true)
    try {
      await apiService.updateUserProfile(userProfile.id, { is_private: checked }, token)
      setIsPrivate(checked)
      toast({
        title: checked ? 'Account set to private' : 'Account set to public',
        description: checked 
          ? 'Your profile is now private. Users need to request to follow you.' 
          : 'Your profile is now public. Anyone can follow you.',
      })
    } catch (error) {
      console.error('Error updating privacy setting:', error)
      toast({
        title: 'Error',
        description: 'Failed to update privacy setting. Please try again.',
        variant: 'destructive',
      })
      setIsPrivate(!checked)
    } finally {
      setIsUpdatingPrivacy(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'uploads' || !isMounted) return

    const run = async () => {
      const token = getToken()
      const q = debouncedUploadSearch

      if (q) {
        if (!token) {
          setUserContent([])
          setSearchNext(null)
          setSearchPage(1)
          return
        }

        if (contentType === 'sound-effects') {
          setContentLoading(true)
          try {
            const all = await apiService.getUserSoundEffects(userProfile.id, token)
            const ql = q.toLowerCase()
            const raw = Array.isArray(all) ? all : []
            setUserContent(
              raw.filter((item: { title?: string; name?: string }) =>
                String(item.title || item.name || '')
                  .toLowerCase()
                  .includes(ql)
              )
            )
          } catch {
            setUserContent([])
          } finally {
            setContentLoading(false)
            setSearchNext(null)
            setSearchPage(1)
          }
          return
        }

        const apiType = mapContentTypeToSearchApiType(contentType)
        if (!apiType) {
          setUserContent([])
          setSearchNext(null)
          return
        }

        setContentLoading(true)
        try {
          const res = await apiService.searchUserContent(userProfile.id, token, {
            q,
            type: apiType,
            page: 1,
            page_size: 20,
          })
          if (isPaginatedUserSearch(res)) {
            setUserContent((res.results as any[]) || [])
            setSearchNext(res.next ?? null)
            setSearchPage(1)
          } else {
            setUserContent([])
            setSearchNext(null)
            setSearchPage(1)
          }
        } catch (error: unknown) {
          console.error('User content search failed:', error)
          toast({
            title: 'Search failed',
            description: 'Could not load results. Try signing in again or try later.',
            variant: 'destructive',
          })
          setUserContent([])
          setSearchNext(null)
        } finally {
          setContentLoading(false)
        }
        return
      }

      setSearchNext(null)
      setSearchPage(1)
      if (!isOwnProfileState && contentType === 'meme-videos') {
        setUserContent([])
        return
      }
      if (!token && contentType !== 'generated-memes') {
        setUserContent([])
        return
      }

      setContentLoading(true)
      try {
        let data: any[] = []
        switch (contentType) {
          case 'meme-videos':
            data = await apiService.getUserMemeVideos(userProfile.id, token!)
            break
          case 'meme-templates':
            data = await apiService.getUserMemeTemplates(userProfile.id, token!)
            break
          case 'gifs':
            data = await apiService.getUserGifs(userProfile.id, token!)
            break
          case 'sound-effects':
            data = await apiService.getUserSoundEffects(userProfile.id, token!)
            break
          case 'generated-memes':
            data = await apiService.getUserGeneratedMemes(userProfile.id, token ?? undefined)
            break
        }
        setUserContent(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error('Error loading user content:', error)
        setUserContent([])
      } finally {
        setContentLoading(false)
      }
    }

    void run()
  }, [
    userProfile.id,
    contentType,
    activeTab,
    isOwnProfileState,
    debouncedUploadSearch,
    isMounted,
  ])

  const loadMoreUploadSearch = useCallback(async () => {
    if (!searchNext || !debouncedUploadSearch) return
    const token = getToken()
    if (!token) return
    if (contentType === 'sound-effects') return
    const apiType = mapContentTypeToSearchApiType(contentType)
    if (!apiType) return

    setContentLoading(true)
    try {
      const res = await apiService.searchUserContent(userProfile.id, token, {
        q: debouncedUploadSearch,
        type: apiType,
        page: searchPage + 1,
        page_size: 20,
      })
      if (isPaginatedUserSearch(res)) {
        setUserContent((prev) => [...prev, ...((res.results as any[]) || [])])
        setSearchNext(res.next ?? null)
        setSearchPage((p) => p + 1)
      }
    } catch (e) {
      console.error(e)
      toast({ title: 'Could not load more', variant: 'destructive' })
    } finally {
      setContentLoading(false)
    }
  }, [
    searchNext,
    debouncedUploadSearch,
    contentType,
    userProfile.id,
    searchPage,
  ])

  const token = getToken()
  const needsAuthForUploads =
    !token && (debouncedUploadSearch.length > 0 || contentType !== 'generated-memes')

  const isProfileOwner =
    authUser?.id != null && Number(authUser.id) === Number(userProfile.id)

  const ownerHasActiveSubscription =
    isProfileOwner &&
    Boolean(
      isPremium ||
      premiumSubscription?.is_active ||
      premiumSubscription?.entitlement_active
    )

  /** Public: GET /users/:id has_premium_plan. Owner: also /user-subscription (same as header "You're Pro"). */
  const profileShowsPremium = profilePremiumFromApi || ownerHasActiveSubscription

  return (
    <>
      <div className="mb-8 rounded-2xl border border-zinc-800/90 bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900/70 p-5 shadow-xl shadow-black/25 sm:p-8">
        <div className="flex items-center gap-4 mb-6">
          <Link href="/">
            <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <Avatar className="h-24 w-24 shrink-0 ring-2 ring-zinc-800 sm:h-28 sm:w-28">
            <AvatarImage
              src={userProfile.profilePicturePath ? getMediaUrl(userProfile.profilePicturePath) : undefined}
              alt={userProfile.user.username}
            />
            <AvatarFallback className="bg-zinc-700 text-white text-2xl">
              {userProfile.user.username.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{userProfile.user.username}</h1>
              {((userProfile.verified === true || userProfile.user.verified === true) || (isMounted && isOwnProfileState && hasFeature("verification_badge"))) && (
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-500" title="Verified">
                  <BadgeCheck className="h-4 w-4 text-white" />
                </span>
              )}
              {profileShowsPremium && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2.5 py-0.5 text-sm font-semibold text-amber-400"
                  title={
                    ownerHasActiveSubscription && premiumSubscription?.plan_title
                      ? `Premium · ${premiumSubscription.plan_title}`
                      : 'Premium subscriber'
                  }
                >
                  <Crown className="h-4 w-4 shrink-0" />
                  Premium
                </span>
              )}
              {userProfile.is_private && (
                <Badge variant="secondary">Private</Badge>
              )}
            </div>

            <div className="flex items-center gap-6 mb-4 text-sm text-zinc-400">
              <span>{userProfile.followers_count} followers</span>
              <span>{userProfile.following_count} following</span>
            </div>

            {isMounted && !isOwnProfileState && pathname.startsWith('/user/') && (
              <div className="flex flex-wrap items-center gap-2">
                {!isBlocked && (
                  <>
                    <FollowButton
                      userId={targetUserId}
                      isFollowing={isFollowing}
                      onFollowChange={handleFollowChange}
                      variant="secondary"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    />
                    {userProfile.user?.roles?.length === 1 && userProfile.user.roles[0] === 'user' && (
                      <Button
                        variant="outline"
                        onClick={() => router.push(`/chat?userId=${targetUserId}`)}
                        className="border-zinc-700 text-white hover:bg-zinc-800"
                      >
                        Message
                      </Button>
                    )}
                  </>
                )}

                <Button
                  variant="outline"
                  onClick={handleBlockToggle}
                  disabled={isBlocking}
                  className={isBlocked
                    ? "border-zinc-700 text-white hover:bg-zinc-800"
                    : "border-red-600 text-red-400 hover:bg-red-500/10"
                  }
                >
                  {isBlocking ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isBlocked ? (
                    <UserCheck className="h-4 w-4 mr-2" />
                  ) : (
                    <UserX className="h-4 w-4 mr-2" />
                  )}
                  {isBlocked ? 'Unblock' : 'Block'}
                </Button>
              </div>
            )}

            {/* Privacy Toggle - Only show on own profile */}
            {isMounted && isOwnProfileState && (
              <div className="flex items-center gap-3 mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                  {isPrivate ? (
                    <Lock className="h-5 w-5 text-zinc-400" />
                  ) : (
                    <Unlock className="h-5 w-5 text-zinc-400" />
                  )}
                  <Label htmlFor="privacy-toggle" className="text-sm font-medium text-zinc-300 cursor-pointer">
                    Private Account
                  </Label>
                </div>
                <Switch
                  id="privacy-toggle"
                  checked={isPrivate}
                  onCheckedChange={handlePrivacyToggle}
                  disabled={isUpdatingPrivacy}
                />
                <span className="text-xs text-zinc-400 ml-auto">
                  {isPrivate ? 'Only approved followers can see your content' : 'Anyone can see your content'}
                </span>
              </div>
            )}

            {isMounted && isOwnProfileState && isPremium && premiumSubscription && (
              <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <Receipt className="h-4 w-4 text-amber-500" />
                  Subscription
                </div>
                <p className="text-zinc-400 text-sm">
                  Plan: <span className="text-white font-medium">{premiumSubscription.plan_title}</span> (Rs. {premiumSubscription.plan_price})
                </p>
                <p className="text-zinc-400 text-sm flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Current period ends: {new Date(premiumSubscription.current_period_end).toLocaleDateString()}
                  {premiumSubscription.days_until_expiry != null && (
                    <span className="text-zinc-500"> ({premiumSubscription.days_until_expiry} days left)</span>
                  )}
                </p>
                <Link href="/premium" className="inline-flex items-center gap-1 text-sm text-red-400 hover:text-red-300 font-medium">
                  Manage plan & invoices
                  <ArrowLeft className="h-4 w-4 rotate-180" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 -mx-1">
        <div className="flex gap-1 overflow-x-auto border-b border-zinc-800 pb-0.5 scrollbar-thin [scrollbar-width:thin]">
          {!isOwnProfileState && (
            <button
              type="button"
              onClick={() => setActiveTab('uploads')}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
                activeTab === 'uploads'
                  ? 'border-b-2 border-white text-white'
                  : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
              )}
            >
              Uploads
            </button>
          )}
          {isOwnProfileState && (
            pathname !== '/profile' && (
              <button
                type="button"
                onClick={() => setActiveTab('videos')}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  activeTab === 'videos'
                    ? 'border-b-2 border-white text-white'
                    : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
                )}
              >
                Videos ({userVideos.length})
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => setActiveTab('followers')}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
              activeTab === 'followers'
                ? 'border-b-2 border-white text-white'
                : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
            )}
          >
            Followers ({userProfile.followers_count})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('following')}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
              activeTab === 'following'
                ? 'border-b-2 border-white text-white'
                : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
            )}
          >
            Following ({userProfile.following_count})
          </button>
          {isMounted && token && isOwnProfileState && (
            <>
              <button
                type="button"
                onClick={() => setActiveTab('uploads')}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  activeTab === 'uploads'
                    ? 'border-b-2 border-white text-white'
                    : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
                )}
              >
                My Uploads
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('requests')}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  activeTab === 'requests'
                    ? 'border-b-2 border-white text-white'
                    : 'border-b-2 border-transparent text-zinc-400 hover:text-zinc-200'
                )}
              >
                Follow Requests
              </button>
            </>
          )}
        </div>
      </div>

      <div>
        {activeTab === 'videos' && isOwnProfileState && pathname !== '/profile' && (
          <div>
            {userVideos.length > 0 ? (
              <VideoGrid videos={userVideos} onVideoClick={handleVideoClick} />
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Users className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No videos yet</h3>
                <p className="text-gray-600">This user hasn&apos;t uploaded any videos.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'followers' && (
          <div className="grid gap-4">
            {followers.map((follower) => (
              <Card key={follower.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:bg-zinc-800/20 p-2 rounded-lg transition-colors"
                    onClick={() => router.push(`/user/${follower.id}`)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={follower.profilePicture ? getMediaUrl(follower.profilePicture) : undefined} alt={follower.username} />
                      <AvatarFallback className="bg-zinc-700 text-white">{follower.username.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{follower.username}</p>
                        {follower.verified && (
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 flex-shrink-0">
                            <BadgeCheck className="h-3 w-3 text-white" />
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400">{follower.followers_count || 0} followers</p>
                    </div>
                  </div>
                  <FollowButton
                    userId={follower.id}
                    variant="outline"
                    size="sm"
                  />
                </div>
              </Card>
            ))}
            {followers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-zinc-400">No followers yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div className="grid gap-4">
            {following.map((followedUser) => (
              <Card key={followedUser.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:bg-zinc-800/20 p-2 rounded-lg transition-colors"
                    onClick={() => router.push(`/user/${followedUser.id}`)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={followedUser.profilePicture ? getMediaUrl(followedUser.profilePicture) : undefined} alt={followedUser.username} />
                      <AvatarFallback className="bg-zinc-700 text-white">{followedUser.username.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{followedUser.username}</p>
                        {(followedUser.verified || Boolean((followedUser as any)?.is_verified)) && (
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 flex-shrink-0">
                            <BadgeCheck className="h-3 w-3 text-white" />
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400">{followedUser.followers_count || 0} followers</p>
                    </div>
                  </div>
                  <FollowButton
                    userId={followedUser.id}
                    isFollowing={true}
                    variant="outline"
                    size="sm"
                  />
                </div>
              </Card>
            ))}
            {following.length === 0 && (
              <div className="text-center py-12">
                <p className="text-zinc-400">Not following anyone yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && isMounted && token && isOwnProfileState && (
          <FollowRequests userId={userProfile.id} />
        )}

        {activeTab === 'uploads' && isMounted && (
          <div className="space-y-5">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 shadow-inner shadow-black/20 sm:p-4">
              <label className="sr-only" htmlFor="profile-upload-search">
                Search uploads
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="profile-upload-search"
                  value={uploadSearchInput}
                  onChange={(e) => setUploadSearchInput(e.target.value)}
                  placeholder="Search titles & descriptions…"
                  className="h-11 border-zinc-700 bg-zinc-950/90 pl-10 pr-10 text-white placeholder:text-zinc-500 focus-visible:ring-red-500/40"
                  autoComplete="off"
                />
                {uploadSearchInput ? (
                  <button
                    type="button"
                    onClick={() => {
                      setUploadSearchInput('')
                      setDebouncedUploadSearch('')
                    }}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-white"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <p className="mt-2 text-[11px] leading-snug text-zinc-500 sm:text-xs">
                Search uses your account and filters by the tab below. Sounds: local filter on loaded items (API search covers videos, templates, GIFs, generated memes).
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={contentType === 'meme-videos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContentType('meme-videos')}
                className={cn(
                  'rounded-full',
                  contentType === 'meme-videos' ? 'bg-red-600 hover:bg-red-500' : 'border-zinc-700'
                )}
              >
                <VideoIcon className="h-4 w-4 mr-2" />
                Videos
              </Button>
              <Button
                type="button"
                variant={contentType === 'meme-templates' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContentType('meme-templates')}
                className={cn(
                  'rounded-full',
                  contentType === 'meme-templates' ? 'bg-red-600 hover:bg-red-500' : 'border-zinc-700'
                )}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Templates
              </Button>
              <Button
                type="button"
                variant={contentType === 'generated-memes' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContentType('generated-memes')}
                className={cn(
                  'rounded-full',
                  contentType === 'generated-memes' ? 'bg-red-600 hover:bg-red-500' : 'border-zinc-700'
                )}
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Memes
              </Button>
              <Button
                type="button"
                variant={contentType === 'gifs' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContentType('gifs')}
                className={cn(
                  'rounded-full',
                  contentType === 'gifs' ? 'bg-red-600 hover:bg-red-500' : 'border-zinc-700'
                )}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                GIFs
              </Button>
              <Button
                type="button"
                variant={contentType === 'sound-effects' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContentType('sound-effects')}
                className={cn(
                  'rounded-full',
                  contentType === 'sound-effects' ? 'bg-red-600 hover:bg-red-500' : 'border-zinc-700'
                )}
              >
                <Music className="h-4 w-4 mr-2" />
                Sounds
              </Button>
            </div>

            {!isOwnProfileState && contentType === 'meme-videos' && !debouncedUploadSearch ? (
              <div>
                {userVideos.length > 0 ? (
                  <VideoGrid videos={userVideos} onVideoClick={handleVideoClick} />
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Users className="w-16 h-16 mx-auto" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">No uploads yet</h3>
                    <p className="text-gray-600">This user hasn&apos;t uploaded any videos.</p>
                  </div>
                )}
              </div>
            ) : needsAuthForUploads ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center space-y-2">
                    <Lock className="h-8 w-8 text-zinc-600" />
                    <h4 className="text-white font-semibold">Sign in required</h4>
                    <p className="text-zinc-500 text-sm max-w-sm">
                      {debouncedUploadSearch
                        ? "Signing in lets you search this member's uploads (API requires authentication)."
                        : "Sign in to view this user's uploads."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {contentLoading ? (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-zinc-800/80 bg-zinc-950/40 py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-red-500 mb-2" />
                    <p className="text-zinc-400 text-sm">Loading content...</p>
                  </div>
                ) : userContent.length > 0 ? (
                  <div className="space-y-4">
                    <UserProfileUploadGrid
                      contentType={contentType}
                      items={userContent}
                      isOwnProfile={isOwnProfileState}
                      profileUser={{
                        id: userProfile.user.id,
                        username: userProfile.user.username,
                        profilePicture:
                          userProfile.user.profilePicture ?? userProfile.profilePicturePath ?? null,
                        verified: !!(userProfile.verified || userProfile.user.verified),
                      }}
                      deletingId={deletingId}
                      onVideoClick={handleVideoClick}
                      onOpenMemeEditor={handleItemClick}
                      onDelete={requestDeleteContent}
                    />
                    {debouncedUploadSearch && searchNext ? (
                      <div className="flex justify-center pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-zinc-600 text-zinc-200 hover:bg-zinc-800"
                          onClick={() => void loadMoreUploadSearch()}
                          disabled={contentLoading}
                        >
                          {contentLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Load more results'
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-800 bg-zinc-950/30 py-12 text-center space-y-3">
                    <div className="p-3 bg-zinc-800/50 rounded-full">
                      <Upload className="h-6 w-6 text-zinc-600" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm">
                        {debouncedUploadSearch
                          ? `No results for "${debouncedUploadSearch}"`
                          : `No ${contentType.replace(/-/g, ' ')} found`}
                      </h4>
                      <p className="text-zinc-500 text-xs">
                        {debouncedUploadSearch
                          ? 'Try another keyword or switch the content tab above.'
                          : isOwnProfileState
                            ? "You haven't uploaded any content in this category yet."
                            : "This user hasn't uploaded any content in this category yet."}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <AuthPopup isOpen={showAuthPopup} onClose={closeAuthPopup} action={authAction} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null)
        }}
        title={`Delete ${pendingDelete?.title ?? 'this item'}?`}
        description="This action can't be undone. The content will be permanently removed from your account."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={confirmDeleteContent}
      />

    </>
  )
}
